import { apiClient } from '@app/api';
import {
  IndividualStationFilterOption,
  OptionType,
} from '@app/components/stations/advanced-filter';
import { useToast } from '@app/core/notifications';
import { useStationsWithAudit } from '@app/modules/audit/hooks/useStationsWithAudit';
import { GipReportParametersForOrderIdResult } from '@mi-front/core';
import { useFormikContext } from 'formik';
import { useState } from 'react';
import { GipReportValues } from '../../types';

export function useLoadParamsForOrderId(orderIds: string[]) {
  const [isLoading, setLoading] = useState(false);
  const [loadedOrderParams, setLoadedOrderParams] =
    useState<GipReportParametersForOrderIdResult | null>(null);
  const fp = useFormikContext<GipReportValues>();
  const { notifyWarn } = useToast();
  const stations = useStationsWithAudit();

  const loadOrderParams = async () => {
    setLoading(true);

    try {
      const params = await apiClient.audit.gipGetParametersForOrderId({
        orderId: orderIds,
      });

      if (!params.stationsIds.length) {
        notifyWarn('No se encontraron schedules asociados a la orden');
        return;
      }

      setLoadedOrderParams(params);

      if (fp.values.stationsSelectionType === 'advanced') {
        await fp.setFieldValue(
          'stationsFilter',
          stations
            .filter((s) => params.stationsIds.includes(s.id))
            .map<IndividualStationFilterOption>((s) => ({
              type: OptionType.IndividualStation,
              value: s,
              label: s.name,
            })),
        );
      } else {
        await fp.setFieldValue(
          'stations',
          stations.filter((s) => params.stationsIds.includes(s.id)),
        );
      }

      await fp.setFieldValue(
        'campaigns',
        params.campaigns.filter((v) => !!v).map((name) => ({ name })),
      );
      await fp.setFieldValue(
        'advertisers',
        params.advertisers.filter((v) => !!v).map((name) => ({ name })),
      );
      await fp.setFieldValue(
        'versions',
        params.versions.filter((v) => !!v).map((name) => ({ name })),
      );

      if (params.schedulesRange.from) {
        await fp.setFieldValue('from', params.schedulesRange.from);
      }

      if (params.schedulesRange.to) {
        await fp.setFieldValue('to', params.schedulesRange.to);
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    loadOrderParams,
    loadedOrderParams,
    isLoading,
  };
}
